/**
 * The demo's conversion moment (SKE-4166, phase 6).
 *
 * Rendered as a sibling of `DemoEmailGate` in Homepage, inside the existing
 * `isDemoMode()` guard, so the real reviewer path never mounts it.
 *
 * A visitor who has just worked a board of approvals is at the top of their
 * interest curve. This is the one surface that asks them to do something about
 * it, and it offers exactly three doors — no more, because a page of options is
 * a page of decisions:
 *
 *   TRIAL    an ordinary link to signup, carrying the campaign the visitor
 *            arrived on (see demoConversionLinks.ts). The primary action.
 *   BOOK     the sales booking page. Hidden entirely when this build has no
 *            booking URL, rather than rendered pointing nowhere.
 *   SUMMARY  the recap by email — the "send me a quote" of a self-serve demo,
 *            and the only door that leaves something behind after the tab
 *            closes.
 *
 * NO EMAIL YET? STILL NOT A DEAD END. The summary step asks for the address
 * inline, prefilled when the visitor gave it to the gate on this page load. It
 * is the same Turnstile-protected posture as the gate — the endpoint sends mail
 * to whatever address it is handed, so the bot gate is not optional — and a
 * successful send unlocks the demo exactly as the gate would, because the thing
 * that unlocks is "we have their email", not which form they typed it into.
 *
 * PLACEMENT. The viewport already carries the gate/personalization slot
 * (bottom-left), Reset demo (bottom-right) and the marketer rail (right). So
 * the offer is a dialog at the moment it is earned, plus one compact pill on
 * screens wide enough to have room for it — never a fourth thing competing for
 * a corner on a phone.
 *
 * Tailwind only (styled-components are lint-blocked in this app), and every
 * transition is `motion-safe:`.
 */

import { useCallback, useMemo, useState } from 'react'

import { Button } from 'ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'ui/dialog'
import { Input } from 'ui/input'
import { Label } from 'ui/label'

import { trackDemo } from './demoAnalytics'
import { getDemoSessionId } from './demoAttribution'
import { buildDemoTrialUrl, getDemoBookingUrl } from './demoConversionLinks'
import { isLikelyEmail } from './demoLead'
import { demoFormat, demoStrings } from './demoStrings'
import {
  buildDemoSummaryPayload,
  isDemoSummaryConfigured,
  submitDemoSummary,
  type DemoSummaryFailureReason,
} from './demoSummary'
import { getCapturedDemoEmail, markDemoUnlocked } from './demoUnlock'
import { useDemoBrand } from './useDemoBrand'
import { useDemoConversion } from './useDemoConversion'
import { useDemoTurnstile } from './useDemoTurnstile'

const ERROR_STRINGS: Record<DemoSummaryFailureReason, string> = {
  'not-configured': demoStrings.ctaSummaryErrorServer,
  'invalid-email': demoStrings.ctaSummaryErrorInvalidEmail,
  captcha: demoStrings.ctaSummaryErrorCaptcha,
  'rate-limited': demoStrings.ctaSummaryErrorRateLimited,
  network: demoStrings.ctaSummaryErrorNetwork,
  server: demoStrings.ctaSummaryErrorServer,
}

/** Which face of the dialog is showing. */
type PanelView = 'choices' | 'summary' | 'sent'

const CARD_CLASS =
  'flex w-full flex-col items-start gap-0.5 rounded-lg border border-neutral-200 bg-white px-4 py-3 text-left hover:border-blue-200 hover:bg-blue-25 motion-safe:transition-colors'

const DemoConversionPanel: React.FC = () => {
  const conversion = useDemoConversion()
  const brand = useDemoBrand()
  const turnstile = useDemoTurnstile()

  const [view, setView] = useState<PanelView>('choices')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sentDomain, setSentDomain] = useState<string | null>(null)

  const bookingUrl = getDemoBookingUrl()
  const isSummaryOffered = isDemoSummaryConfigured()

  // Built once per open rather than on every keystroke; the attribution it
  // reads is pinned for the visit, so there is nothing to recompute.
  const trialUrl = useMemo(
    () => (conversion.isOpen ? buildDemoTrialUrl() : ''),
    [conversion.isOpen]
  )

  // Memoised so the handlers below keep a stable identity between renders —
  // every one of them carries this object as an analytics dependency.
  const ctaProps = useMemo(
    () => ({
      trigger: conversion.trigger,
      action_count: conversion.actionCount,
      approved: conversion.progress.approved,
      request_change: conversion.progress.requestChange,
    }),
    [
      conversion.trigger,
      conversion.actionCount,
      conversion.progress.approved,
      conversion.progress.requestChange,
    ]
  )

  const handleTrial = useCallback(() => {
    trackDemo('demo-cta-trial', ctaProps)
    // Not preventing the default navigation: the anchor is a real link so it
    // still works if this handler throws, and middle-click/open-in-new-tab
    // behave the way a link should.
  }, [ctaProps])

  const handleBookCall = useCallback(() => {
    trackDemo('demo-cta-book-call', ctaProps)
  }, [ctaProps])

  const openSummary = useCallback(() => {
    setError(null)
    setEmail((current) => current || getCapturedDemoEmail() || '')
    setView('summary')
  }, [])

  const fail = useCallback(
    (reason: DemoSummaryFailureReason) => {
      setError(ERROR_STRINGS[reason])
      trackDemo('demo-cta-email-summary-failed', { reason, ...ctaProps })
    },
    [ctaProps]
  )

  const handleSummarySubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()

      if (isSubmitting) {
        return
      }

      const candidate = email.trim()

      if (!isLikelyEmail(candidate)) {
        fail('invalid-email')
        return
      }

      // Turnstile only blocks when it is actually configured for this build;
      // where it isn't, the server decides whether a tokenless send is allowed.
      if (turnstile.isEnabled && !turnstile.token) {
        fail('captcha')
        return
      }

      setError(null)
      setIsSubmitting(true)

      const result = await submitDemoSummary(
        buildDemoSummaryPayload({
          email: candidate,
          turnstileToken: turnstile.token || undefined,
          brandName: brand?.name,
          counts: {
            total: conversion.progress.total,
            approved: conversion.progress.approved,
            requestChange: conversion.progress.requestChange,
          },
          sessionId: getDemoSessionId(),
        })
      )

      setIsSubmitting(false)

      if (!result.ok) {
        // A single-use token may already have been spent at siteverify, so on
        // ANY server-side failure force a fresh challenge before a retry — a
        // reused token comes back captcha-failed and dead-ends the visitor.
        if (turnstile.isEnabled) {
          turnstile.reset()
        }
        fail(result.reason)
        return
      }

      // Giving us an address here is the same fact the gate captures, so it
      // unlocks the same things — a visitor who reached the demo through this
      // door must not then be asked for their email a second time.
      markDemoUnlocked(candidate)
      setSentDomain(candidate.split('@')[1]?.toLowerCase() ?? null)
      setView('sent')

      trackDemo('demo-cta-email-summary', {
        ...ctaProps,
        email_domain: candidate.split('@')[1]?.toLowerCase() ?? null,
      })
    },
    [
      brand?.name,
      conversion.progress,
      ctaProps,
      email,
      fail,
      isSubmitting,
      turnstile,
    ]
  )

  const close = useCallback(() => {
    conversion.dismiss()
    // Reopening should land on the choices, not on a stale confirmation.
    setView('choices')
  }, [conversion])

  const isCleared = conversion.trigger === 'cleared'

  return (
    <>
      {conversion.isReachable && !conversion.isOpen ? (
        <button
          type="button"
          onClick={() => {
            setView('choices')
            conversion.open()
          }}
          aria-label={demoStrings.ctaOpenAriaLabel}
          data-testid="demo-cta-open"
          // Only from `lg` up: below that the bottom row already carries the
          // gate and Reset demo, and a third floating control would collide.
          className="fixed bottom-4 left-1/2 z-40 hidden -translate-x-1/2 rounded-full border border-blue-100 bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-md hover:bg-blue-700 motion-safe:transition-colors lg:block"
        >
          {demoStrings.ctaOpenLabel}
        </button>
      ) : null}

      <Dialog
        open={conversion.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            close()
          }
        }}
      >
        <DialogContent
          className="sm:max-w-lg"
          aria-label={demoStrings.ctaAriaLabel}
          data-testid="demo-cta-dialog"
        >
          {view === 'sent' ? (
            <>
              <DialogHeader>
                <DialogTitle>{demoStrings.ctaSummarySentTitle}</DialogTitle>
                <DialogDescription>
                  {sentDomain
                    ? demoFormat('ctaSummarySentBody', { domain: sentDomain })
                    : demoStrings.ctaSummarySentGenericBody}
                </DialogDescription>
              </DialogHeader>

              <DialogFooter className="mt-4">
                <Button type="button" variant="ghost" onClick={close}>
                  {demoStrings.ctaDismissLabel}
                </Button>
                <Button asChild variant="blue-primary">
                  <a
                    href={trialUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={handleTrial}
                    data-testid="demo-cta-trial"
                  >
                    {demoStrings.ctaTrialLabel}
                  </a>
                </Button>
              </DialogFooter>
            </>
          ) : view === 'summary' ? (
            <form onSubmit={handleSummarySubmit} noValidate>
              <DialogHeader>
                <DialogTitle>{demoStrings.ctaSummaryTitle}</DialogTitle>
                <DialogDescription>
                  {demoStrings.ctaSummaryBody}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 flex flex-col gap-2">
                <Label htmlFor="demo-cta-email">
                  {demoStrings.ctaSummaryEmailLabel}
                </Label>
                <Input
                  id="demo-cta-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder={demoStrings.ctaSummaryEmailPlaceholder}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                    setError(null)
                  }}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'demo-cta-error' : undefined}
                />

                {/* Container is only in the tree when a site key exists; the
                    hook's callback ref renders the widget into it. */}
                {turnstile.isEnabled ? (
                  <div ref={turnstile.turnstileRef} className="mt-1" />
                ) : null}

                {error ? (
                  <p
                    id="demo-cta-error"
                    role="alert"
                    className="text-sm text-red-600"
                  >
                    {error}
                  </p>
                ) : null}
              </div>

              <DialogFooter className="mt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setError(null)
                    setView('choices')
                  }}
                >
                  {demoStrings.ctaSummaryBackLabel}
                </Button>
                <Button
                  type="submit"
                  variant="blue-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? demoStrings.ctaSummarySubmittingLabel
                    : demoStrings.ctaSummarySubmitLabel}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {isCleared
                    ? demoStrings.ctaClearedTitle
                    : demoStrings.ctaEngagedTitle}
                </DialogTitle>
                <DialogDescription>
                  {isCleared
                    ? demoFormat('ctaClearedBody', {
                        approved: conversion.progress.approved,
                        changes: conversion.progress.requestChange,
                      })
                    : demoStrings.ctaEngagedBody}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 flex flex-col gap-2">
                <a
                  href={trialUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={handleTrial}
                  data-testid="demo-cta-trial"
                  className="flex w-full flex-col items-start gap-0.5 rounded-lg border border-blue-600 bg-blue-600 px-4 py-3 text-left hover:bg-blue-700 motion-safe:transition-colors"
                >
                  <span className="text-sm font-semibold text-white">
                    {demoStrings.ctaTrialLabel}
                  </span>
                  <span className="text-xs text-blue-50">
                    {demoStrings.ctaTrialHint}
                  </span>
                </a>

                {bookingUrl ? (
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={handleBookCall}
                    data-testid="demo-cta-book-call"
                    className={CARD_CLASS}
                  >
                    <span className="text-sm font-semibold text-neutral-800">
                      {demoStrings.ctaBookLabel}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {demoStrings.ctaBookHint}
                    </span>
                  </a>
                ) : null}

                {isSummaryOffered ? (
                  <button
                    type="button"
                    onClick={openSummary}
                    data-testid="demo-cta-summary"
                    className={CARD_CLASS}
                  >
                    <span className="text-sm font-semibold text-neutral-800">
                      {demoStrings.ctaSummaryLabel}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {demoStrings.ctaSummaryHint}
                    </span>
                  </button>
                ) : null}
              </div>

              <DialogFooter className="mt-4">
                <Button type="button" variant="ghost" onClick={close}>
                  {demoStrings.ctaDismissLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DemoConversionPanel
